import { z } from "zod";

import { createMutationRequestDefinition } from "~/lib/network/definitions/create";
import { queryClient } from "~/lib/query-client";
import { creditsBalanceQuery } from "~/modules/credits/network/queries";

import {
  getProjectDetailsQuery,
  listAuditionsForAudioQuery,
  listProjectsQuery,
  listProjectVideosQuery,
} from "./queries";
import { updateProjectDetailsInCache } from "./utils";

export const createProjectAudioMutation = createMutationRequestDefinition({
  host: "cap-srv",
  method: "POST",
  path: "mirage-studio/audio/clip-ranges/create/submit",
  paramsSchema: z.object({
    audioAssetId: z.string(),
    projectId: z.string().nullable(),
    ttsId: z.string().optional().nullable().default(null),
  }),
  responseSchema: z.object({
    operationId: z.string(),
    projectId: z.string(),
  }),
  onSuccess({ projectId }, { params: { projectId: previousProjectId } }) {
    if (!previousProjectId) {
      // a new project was created as a side effect
      listProjectsQuery.insertItemInQueryData(
        queryClient,
        { params: {} },
        "projects",
        {
          id: projectId,
          title: "",
          videoCount: 0,
          createdAt: new Date().toISOString(),
          thumbnailUrl: null,
          latestAudioId: null,
          completedActions: {},
        },
        "start"
      );
    }

    // if there was no project id, a project gets created as a side effect
    // however, we do not need to block on this returning since it does not immediately impact the UI
    listProjectsQuery.invalidateQuery(queryClient, { params: {} });
  },
});

export const setClipRangesMutation = createMutationRequestDefinition({
  host: "cap-srv",
  method: "POST",
  path: "mirage-studio/audio/clip-ranges/set",
  paramsSchema: z.object({
    audioId: z.string(),
    projectId: z.string(),
    clipRanges: z.array(
      z.object({
        startMs: z.number(),
        endMs: z.number(),
      })
    ),
  }),
  responseSchema: z.object({}),
  onSuccess() {
    // once this is done, lazily invalidate the project list query
    // to update link due to change in `completedActions`
    listProjectsQuery.invalidateQuery(queryClient, { params: {} });
  },
});

export const createAuditionsFromAudioMutation = createMutationRequestDefinition({
  host: "cap-srv",
  method: "POST",
  path: "mirage-studio/auditions/create-from-audio",
  paramsSchema: z.object({
    audioId: z.string(),
    count: z.number(),
    projectId: z.string(),
    actorId: z.string().optional().nullable(),
  }),
  responseSchema: z.object({
    auditionOperationIds: z.array(z.string()),
  }),
  onSuccess(_, { params: { audioId, projectId } }) {
    // creating an audition uses credits
    creditsBalanceQuery.invalidateQuery(queryClient);

    return listAuditionsForAudioQuery.invalidateQuery(queryClient, {
      params: { projectId, audioId },
    });
  },
});

export const createAuditionsFromAudioWithPromptMutation = createMutationRequestDefinition({
  host: "cap-srv",
  method: "POST",
  path: "/mirage-studio/auditions/create-from-audio-with-prompt",
  paramsSchema: z.object({
    projectId: z.string(),
    audioId: z.string(),
    count: z.number(),
    characterPrompt: z.string(),
    backgroundPrompt: z.string(),
  }),
  responseSchema: z.object({
    auditionOperationIds: z.array(z.string()),
  }),
  onSuccess(_, { params: { audioId, projectId } }) {
    // creating an audition uses credits
    creditsBalanceQuery.invalidateQuery(queryClient);

    return listAuditionsForAudioQuery.invalidateQuery(queryClient, {
      params: { projectId, audioId },
    });
  },
});

export const cancelAuditionMutation = createMutationRequestDefinition({
  host: "cap-srv",
  method: "POST",
  path: "/mirage-studio/audition/cancel",
  paramsSchema: z.object({
    auditionId: z.string(),
    projectId: z.string(),
    audioId: z.string(),
  }),
  responseSchema: z.object({}),
  onSuccess(_, { params: { projectId, audioId, auditionId } }) {
    listAuditionsForAudioQuery.setQueryData(
      queryClient,
      {
        params: { projectId, audioId },
      },
      (old) => {
        if (!old) {
          return old;
        }

        return {
          ...old,
          auditions: old.auditions.filter((audition) => audition.id !== auditionId),
        };
      }
    );

    listAuditionsForAudioQuery.invalidateQuery(queryClient, {
      params: { projectId, audioId },
    });

    creditsBalanceQuery.invalidateQuery(queryClient);
  },
});

export const generateVideoFromAuditionMutation = createMutationRequestDefinition({
  host: "cap-srv",
  method: "POST",
  path: "/mirage-studio/video/generate/from-audition/submit",
  paramsSchema: z.object({
    auditionId: z.string(),
    projectId: z.string(),
  }),
  responseSchema: z.object({
    videoOperationId: z.string(),
  }),
  onSuccess({ videoOperationId }, { params: { projectId } }) {
    // optimistically insert the video at the start of the list
    listProjectVideosQuery.insertItemInQueryData(
      queryClient,
      { params: { projectId } },
      "videos",
      { id: videoOperationId, state: "QUEUED", createdAt: new Date().toISOString() },
      "start"
    );

    // invalidate the video list query to get the latest data
    listProjectVideosQuery.invalidateQuery(queryClient, { params: { projectId } });

    // we need to invalidate the project list because it cares about how many videos
    // a project has, but do not need to block
    listProjectsQuery.invalidateQuery(queryClient, { params: {} });

    // generating a video uses credits
    creditsBalanceQuery.invalidateQuery(queryClient);
  },
});

export const cancelVideoGenerationMutation = createMutationRequestDefinition({
  host: "cap-srv",
  method: "POST",
  path: "/mirage-studio/video/generate/from-audition/cancel",
  paramsSchema: z.object({
    videoOperationId: z.string(),
    projectId: z.string(),
  }),
  responseSchema: z.object({}),
  onSuccess(_, { params: { projectId, videoOperationId } }) {
    // we need to invalidate the project list because it cares about video count
    listProjectsQuery.invalidateQuery(queryClient, { params: {} });

    // cancelling will result in a credit refund
    creditsBalanceQuery.invalidateQuery(queryClient);

    // optimistically remove the video from the list
    listProjectVideosQuery.removeItemFromQueryData(
      queryClient,
      { params: { projectId } },
      "videos",
      (video) => video.id === videoOperationId
    );

    // invalidate the query to get the latest data
    listProjectVideosQuery.invalidateQuery(queryClient, { params: { projectId } });
  },
});

export const deleteProjectMutation = createMutationRequestDefinition({
  host: "cap-srv",
  method: "POST",
  path: "mirage-studio/project/delete",
  paramsSchema: z.object({
    projectId: z.string(),
  }),
  responseSchema: z.object({}),
  onSuccess(_, { params: { projectId } }) {
    listProjectsQuery.removeItemFromQueryData(
      queryClient,
      { params: {} },
      "projects",
      (project) => project.id === projectId
    );

    // invalidate the query to get the latest data
    listProjectsQuery.invalidateQuery(queryClient, { params: {} });
  },
});

export const renameProjectMutation = createMutationRequestDefinition({
  host: "cap-srv",
  method: "POST",
  path: "mirage-studio/project/rename",
  paramsSchema: z.object({
    projectId: z.string(),
    name: z.string(),
  }),
  responseSchema: z.object({}),
  onSuccess(_, { params: { projectId } }) {
    // NOTE: on this query, in the `onMutate` we actually update the project name
    // optimistically, so we don't need to do anything here besides invalidate the query

    // invalidate the query to get the latest data
    listProjectsQuery.invalidateQuery(queryClient, { params: {} });

    // invalidate the project details query to get the latest data
    getProjectDetailsQuery.invalidateQuery(queryClient, { params: { id: projectId } });
  },
});

export const regenerateVideoClipMutation = createMutationRequestDefinition({
  host: "cap-srv",
  method: "POST",
  path: "/mirage-studio/video/clip/regenerate/submit",
  paramsSchema: z.object({
    videoId: z.string(),
    clipIndex: z.number(),
    projectId: z.string(),
  }),
  responseSchema: z.object({
    clipId: z.string(),
  }),
  onSuccess() {
    // regenerating a video uses credits
    creditsBalanceQuery.invalidateQuery(queryClient);
  },
});

export const selectVideoClipForIndexMutation = createMutationRequestDefinition({
  host: "cap-srv",
  method: "POST",
  path: "/mirage-studio/video/clip/select",
  paramsSchema: z.object({
    projectId: z.string(),
    videoId: z.string(),
    clipIndex: z.number(),
    clipId: z.string(),
  }),
  responseSchema: z.object({}),
});

export const exportVideoForClipsMutation = createMutationRequestDefinition({
  host: "cap-srv",
  method: "POST",
  path: "/mirage-studio/video/export-for-clips",
  paramsSchema: z.object({
    videoId: z.string(),
    clipIds: z.array(z.string()),
  }),
  responseSchema: z.object({
    url: z.string(),
  }),
});

export const exportVideoForVideoIdMutation = createMutationRequestDefinition({
  host: "cap-srv",
  method: "POST",
  path: "/mirage-studio/video/export",
  paramsSchema: z.object({
    videoId: z.string(),
  }),
  responseSchema: z.object({
    url: z.string(),
  }),
});

export const removeVideoMutation = createMutationRequestDefinition({
  host: "cap-srv",
  method: "POST",
  path: "/mirage-studio/project/video/remove",
  paramsSchema: z.object({
    projectId: z.string(),
    videoId: z.string(),
  }),
  responseSchema: z.object({}),
  onSuccess(_, { params: { projectId, videoId } }) {
    // optimistically remove the video from the list
    listProjectVideosQuery.removeItemFromQueryData(
      queryClient,
      { params: { projectId } },
      "videos",
      (video) => video.id === videoId
    );

    // invalidate the query to get the latest data
    listProjectVideosQuery.invalidateQuery(queryClient, { params: { projectId } });

    // optimistically update the project list and details count data
    updateProjectDetailsInCache(projectId, (previousFields) => ({
      videoCount: previousFields.videoCount - 1,
    }));

    getProjectDetailsQuery.invalidateQuery(queryClient, { params: { id: projectId } });
    listProjectsQuery.invalidateQuery(queryClient, { params: {} });
  },
});
